import React, { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Viewer from 'react-viewer';
import { ImageDecorator } from 'react-viewer/lib/ViewerProps';
import { Constants } from 'src/constants';
import { ACTIVITY_SESSION_TYPE_KOR } from 'src/constants/activityv3.enum';
import { ActivitySession, StudentActivitySession } from 'src/generated/model';
import { DocumentObject } from 'src/type/document-object';
import { ImageObject } from 'src/type/image-object';
import { getSessionTimeInfo, isEnded } from 'src/util/activityv3';
import { getFileNameFromUrl, isPdfFile } from 'src/util/file';
import { DocumentObjectComponent } from '../DocumentObjectComponent';
import { BadgeV2 } from '../common/BadgeV2';
import { Typography } from '../common/Typography';
import SVGIcon from '../icon/SVGIcon';

interface ActivitySessionDetailViewProps {
  activitySession: ActivitySession;
  studentActivity?: StudentActivitySession;
  dummyImages?: Map<number, ImageObject>;
  dummyFiles?: Map<number, DocumentObject>;
}

export const ActivitySessionDetailView: React.FC<ActivitySessionDetailViewProps> = ({
  activitySession: activity,
  studentActivity,
  dummyImages,
  dummyFiles,
}) => {
  const [hasImagesModalOpen, setImagesModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const viewerImages: ImageDecorator[] = [];
  const timeInfo = getSessionTimeInfo(activity);
  const isLateSubmission =
    studentActivity?.submittedAt &&
    activity.endDate &&
    new Date(activity.endDate) < new Date(studentActivity?.submittedAt);

  if (activity?.images) {
    for (const image of activity.images) {
      if (isPdfFile(image) == false) {
        viewerImages.push({
          src: `${Constants.imageUrl}${image}`,
        });
      }
    }
  }

  return (
    <main className="px-5">
      {/* 데이터 출력 부분 */}
      <div className="flex flex-col rounded-xl border border-primary-gray-200 bg-primary-gray-50">
        {/* 컨텐츠 제목 및 내용 */}
        <div className="flex flex-col gap-2 px-5 py-5">
          <nav className="flex flex-row items-center gap-1">
            <BadgeV2 type="line" color="gray" size={20}>
              {activity.activitySessionOrder?.order || 1}
            </BadgeV2>
            <BadgeV2
              type="solid_strong"
              color={activity.type === 'NOTICE' ? 'dark_green' : activity.type === 'SURVEY' ? 'brown' : 'navy'}
              size={20}
            >
              {ACTIVITY_SESSION_TYPE_KOR[activity.type]}
            </BadgeV2>
            {activity.type !== 'NOTICE' && (
              <>
                <BadgeV2 type="solid_regular" color={isEnded(activity.endDate) ? 'navy' : 'orange'} size={20}>
                  {isEnded(activity.endDate) ? '종료' : '진행중'}
                </BadgeV2>
                <BadgeV2
                  type="line"
                  color={!studentActivity?.isSubmitted ? 'red' : isLateSubmission ? 'orange' : 'green'}
                  size={20}
                >
                  {!studentActivity?.isSubmitted ? '미제출' : isLateSubmission ? '지각제출' : '제출완료'}
                </BadgeV2>
              </>
            )}
          </nav>
          <Typography variant="title2" className="font-semibold">
            {activity?.title}
          </Typography>
          {activity?.content && (
            <Typography variant="body3" className="text-primary-gray-700">
              {activity.content}
            </Typography>
          )}

          {/* 첨부파일 */}
          {(activity?.images?.length > 0 || activity?.files?.length > 0 || dummyFiles || dummyImages) && (
            <div className="flex flex-col gap-3">
              {dummyImages && (
                <div className="flex w-full overflow-x-auto">
                  <div className="flex flex-row gap-2">
                    {dummyImages &&
                      [...dummyImages].map(([key, value]) => (
                        <div className="h-20 w-20 flex-shrink-0 cursor-pointer rounded-lg" key={key}>
                          <LazyLoadImage
                            src={
                              value.image instanceof File
                                ? URL.createObjectURL(value.image)
                                : `${Constants.imageUrl}${value.image}`
                            }
                            alt=""
                            loading="lazy"
                            className="h-full w-full rounded-lg object-cover"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
              {activity?.images && activity?.images.length > 0 && (
                <div className="flex w-full overflow-x-auto">
                  <div className="flex flex-row gap-2">
                    {activity.images.map((image: string, i: number) => (
                      <div
                        key={i}
                        className="h-20 w-20 flex-shrink-0 cursor-pointer rounded-lg"
                        onClick={() => {
                          setActiveIndex(i);
                          setImagesModalOpen(true);
                        }}
                      >
                        <LazyLoadImage
                          src={`${Constants.imageUrl}${image}`}
                          alt=""
                          loading="lazy"
                          className="h-full w-full rounded-lg object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dummyFiles &&
                [...dummyFiles].map(([key, value]) => (
                  <DocumentObjectComponent key={key} id={key} documentObjet={value} />
                ))}
              {activity.files && activity.files.length > 0 && (
                <div className="flex w-full flex-wrap gap-2">
                  {activity.files.map((fileUrl: string, index: number) => (
                    <a
                      href={`${Constants.imageUrl}${fileUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      download={getFileNameFromUrl(fileUrl)}
                      key={index}
                      className="flex h-10 w-max items-center gap-2 rounded-lg border border-primary-gray-200 bg-white px-3 py-2"
                    >
                      <SVGIcon.Link size={16} weight="bold" color="gray700" />
                      <Typography variant="body2" className="line-clamp-1 text-primary-gray-700">
                        {getFileNameFromUrl(fileUrl)}
                      </Typography>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {activity.type !== 'NOTICE' && (
          <>
            {(timeInfo.timeText || timeInfo.hasSubmissionTimeWindow) && (
              <div className="flex flex-col gap-1 border-t border-t-primary-gray-200 px-5 py-3">
                {timeInfo.timeText && (
                  <div className="flex flex-row items-center justify-between">
                    <Typography variant="body2" className="text-primary-gray-600">
                      {timeInfo.timeText}
                    </Typography>
                    <Typography variant="body2" className="font-medium">
                      {timeInfo.formattedDate}
                    </Typography>
                  </div>
                )}

                {/* 제출 가능 시간대 정보 표시 */}
                {timeInfo.hasSubmissionTimeWindow && (
                  <div className="flex flex-row items-center justify-between">
                    <Typography variant="body2" className="text-primary-gray-600">
                      제출 가능 시간대
                    </Typography>
                    <Typography variant="body2" className="font-medium">
                      {timeInfo.submissionTimeWindow}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* 이미지 뷰어 부분 */}
      <div className="absolute">
        <Viewer
          visible={hasImagesModalOpen}
          rotatable
          noImgDetails
          scalable={false}
          images={viewerImages}
          onChange={(activeImage, index) => setActiveIndex(index)}
          onClose={() => setImagesModalOpen(false)}
          activeIndex={activeIndex}
        />
      </div>
    </main>
  );
};
